import { Notification } from "@/utils/notificationUtils";
import {
  InfiniteData,
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { AgentEducationsRequest } from "../../../modules/agent/types";
import { dashboardApi } from "../../../modules/dashboard/api";
import {
  CreateDashboardGoal,
  CreateUpdateDashboardContact,
  CreateUpdateDashboardNotice,
  DashboardCommissionsRequest,
  DashboardContactSearchRequest,
  DashboardGoalRequest,
  DashboardMinMaxDatesRequest,
  DashboardNoticeFilterPageRequest,
  DashboardNoticeFilterPageResult,
  UpdateDashboardGoal,
  UpdateDashboardWidgetSettings,
} from "../../../modules/dashboard/types";
import { UUID } from "../../../typings/global";
import { PageSizes } from "../../constants";
import { EntityObject, TwoLevelEntityIdObject, TwoLevelEntityObject } from "../../types";

const THREE_HOURS = 180 * 60 * 1000;
const SIX_HOURS = 360 * 60 * 1000;

export const DASHBOARD_QUERY_KEYS = {
  getDashboardStatistics: () => ["getDashboardStatistics"],
  getDashboardExtendedStatistics: () => ["getDashboardExtendedStatistics"],
  getDashboardPersonalDates: (payload?: DashboardMinMaxDatesRequest) =>
    payload ? ["getDashboardPersonalDates", payload] : ["getDashboardPersonalDates"],
  getDashboardAgentExpertises: () => ["getDashboardAgentExpertises"],
  getDashboardAgentEducations: (payload?: AgentEducationsRequest) =>
    payload ? ["getDashboardAgentEducations", payload] : ["getDashboardAgentEducations"],
  getDashboardContractStatuses: () => ["getDashboardContractStatuses"],
  getDashboardContractStatistics: (payload?: DashboardMinMaxDatesRequest) =>
    payload ? ["getDashboardContractStatistics", payload] : ["getDashboardContractStatistics"],
  getDashboardMlmStructure: () => ["getDashboardMlmStructure"],
  getDashboardEvents: (payload?: DashboardMinMaxDatesRequest) =>
    payload ? ["getDashboardEvents", payload] : ["getDashboardEvents"],
  getDashboardCommissions: (payload?: DashboardCommissionsRequest) =>
    payload ? ["getDashboardCommissions", payload] : ["getDashboardCommissions"],
  getDashboardPortfolio: (payload?: DashboardMinMaxDatesRequest) =>
    payload ? ["getDashboardPortfolio", payload] : ["getDashboardPortfolio"],
  filterDashboardNotices: (filter?: DashboardNoticeFilterPageRequest) =>
    filter ? ["filterDashboardNotices", filter] : ["filterDashboardNotices"],
  createDashboardNotice: () => ["createDashboardNotice"],
  updateDashboardNotice: () => ["updateDashboardNotice"],
  deleteDashboardNotice: () => ["deleteDashboardNotice"],
  markAsSeenDashboardNotices: () => ["markAsSeenDashboardNotices"],
  markAllAsSeenDashboardNotices: () => ["markAllAsSeenDashboardNotices"],
  toggleSeenDashboardNotice: () => ["toggleSeenDashboardNotice"],
  addDashboardNoticeAttachments: () => ["addDashboardNoticeAttachments"],
  deleteDashboardNoticeAttachment: () => ["deleteDashboardNoticeAttachment"],
  searchDashboardContacts: (filter?: DashboardContactSearchRequest) =>
    filter ? ["searchDashboardContacts", filter] : ["searchDashboardContacts"],
  createDashboardContact: () => ["createDashboardContact"],
  updateDashboardContact: () => ["updateDashboardContact"],
  deleteDashboardContact: () => ["deleteDashboardContact"],
  getDashboardGoals: (payload?: EntityObject<DashboardGoalRequest>) =>
    payload ? ["getDashboardGoals", payload] : ["getDashboardGoals"],
  createDashboardGoal: () => ["createDashboardGoal"],
  updateDashboardGoal: () => ["updateDashboardGoal"],
  deleteDashboardGoal: () => ["deleteDashboardGoal"],
  getDashboardWidgetSettings: () => ["getDashboardWidgetSettings"],
  updateDashboardWidgetSettings: () => ["updateDashboardWidgetSettings"],
  getDefaultDashboardWidgetSettings: () => ["getDefaultDashboardWidgetSettings"],
};

export const useGetDashboardStatistics = () => {
  return useQuery({
    queryKey: DASHBOARD_QUERY_KEYS.getDashboardStatistics(),
    meta: { globalSpinner: false },
    queryFn: async () => {
      const { data } = await dashboardApi.getDashboardStatistics();

      return data;
    },
    staleTime: THREE_HOURS,
  });
};

export const useGetDashboardExtendedStatistics = () => {
  return useQuery({
    queryKey: DASHBOARD_QUERY_KEYS.getDashboardExtendedStatistics(),
    meta: { globalSpinner: false },
    queryFn: async () => {
      const { data } = await dashboardApi.getDashboardExtendedStatistics();

      return data;
    },
    staleTime: THREE_HOURS,
  });
};

export const useGetDashboardPersonalDates = (payload: DashboardMinMaxDatesRequest) => {
  return useQuery({
    queryKey: DASHBOARD_QUERY_KEYS.getDashboardPersonalDates(payload),
    meta: { globalSpinner: false },
    queryFn: async () => {
      const { data } = await dashboardApi.getDashboardPersonalDates(payload);

      return data;
    },
    staleTime: THREE_HOURS,
  });
};

export const useGetDashboardAgentExpertises = () => {
  return useQuery({
    queryKey: DASHBOARD_QUERY_KEYS.getDashboardAgentExpertises(),
    meta: { globalSpinner: false },
    queryFn: async () => {
      const { data } = await dashboardApi.getDashboardAgentExpertises();

      return data;
    },
    staleTime: THREE_HOURS,
  });
};

export const useGetDashboardAgentEducations = (payload: AgentEducationsRequest) => {
  return useQuery({
    queryKey: DASHBOARD_QUERY_KEYS.getDashboardAgentEducations(payload),
    meta: { globalSpinner: false },
    queryFn: async () => {
      const { data } = await dashboardApi.getDashboardAgentEducations(payload);

      return data;
    },
    staleTime: THREE_HOURS,
  });
};

export const useGetDashboardContractStatuses = () => {
  return useQuery({
    queryKey: DASHBOARD_QUERY_KEYS.getDashboardContractStatuses(),
    meta: { globalSpinner: false },
    queryFn: async () => {
      const { data } = await dashboardApi.getDashboardContractStatuses();

      return data;
    },
    staleTime: THREE_HOURS,
  });
};

export const useGetDashboardContractStatistics = (payload: DashboardMinMaxDatesRequest) => {
  return useQuery({
    queryKey: DASHBOARD_QUERY_KEYS.getDashboardContractStatistics(payload),
    meta: { globalSpinner: false },
    queryFn: async () => {
      const { data } = await dashboardApi.getDashboardContractStatistics(payload);

      return data;
    },
    staleTime: THREE_HOURS,
  });
};

export const useGetDashboardMlmStructure = () => {
  return useQuery({
    queryKey: DASHBOARD_QUERY_KEYS.getDashboardMlmStructure(),
    meta: { globalSpinner: false },
    queryFn: async () => {
      const { data } = await dashboardApi.getDashboardMlmStructure();

      return data;
    },
    staleTime: THREE_HOURS,
  });
};

export const useGetDashboardCommissions = (payload: DashboardCommissionsRequest) => {
  return useQuery({
    queryKey: DASHBOARD_QUERY_KEYS.getDashboardCommissions(payload),
    meta: { globalSpinner: false },
    queryFn: async () => {
      const { data } = await dashboardApi.getDashboardCommissions(payload);

      return data;
    },
    staleTime: SIX_HOURS,
  });
};

export const useGetDashboardEvents = (payload: DashboardMinMaxDatesRequest) => {
  return useQuery({
    queryKey: DASHBOARD_QUERY_KEYS.getDashboardEvents(payload),
    meta: { globalSpinner: false },
    queryFn: async () => {
      const { data } = await dashboardApi.getDashboardEvents(payload);

      return data;
    },
    staleTime: THREE_HOURS,
  });
};

export const useGetDashboardPortfolio = (payload: DashboardMinMaxDatesRequest) => {
  return useQuery({
    queryKey: DASHBOARD_QUERY_KEYS.getDashboardPortfolio(payload),
    meta: { globalSpinner: false },
    queryFn: async () => {
      const { data } = await dashboardApi.getDashboardPortfolio(payload);

      return data;
    },
    staleTime: THREE_HOURS,
  });
};

export const useInfiniteFilterDashboardNotices = (filter?: DashboardNoticeFilterPageRequest) => {
  return useInfiniteQuery({
    queryKey: DASHBOARD_QUERY_KEYS.filterDashboardNotices(filter),
    meta: { globalSpinner: false },
    queryFn: async ({ pageParam }) => {
      const { data } = await dashboardApi.filterDashboardNotices(pageParam);

      return data;
    },
    initialPageParam: { pageIndex: 0, pageSize: PageSizes.HUGE, ...filter },
    getNextPageParam: (lastPage) => {
      if (!lastPage.isLast) {
        return { pageIndex: lastPage.pageIndex + 1, pageSize: lastPage.pageSize };
      }

      return;
    },
  });
};

export const useCreateDashboardNotice = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: DASHBOARD_QUERY_KEYS.createDashboardNotice(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: CreateUpdateDashboardNotice) => {
      const { data } = await dashboardApi.createDashboardNotice(payload);

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DASHBOARD_QUERY_KEYS.filterDashboardNotices() });
      Notification.itemCreatedNotification();
    },
  });
};

export const useUpdateDashboardNotice = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: DASHBOARD_QUERY_KEYS.updateDashboardNotice(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: EntityObject<CreateUpdateDashboardNotice>) => {
      const { data } = await dashboardApi.updateDashboardNotice(payload);

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DASHBOARD_QUERY_KEYS.filterDashboardNotices() });
      Notification.itemUpdatedNotification();
    },
  });
};

export const useDeleteDashboardNotice = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: DASHBOARD_QUERY_KEYS.deleteDashboardNotice(),
    mutationFn: (id: string) => {
      return dashboardApi.deleteDashboardNotice({ id });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DASHBOARD_QUERY_KEYS.filterDashboardNotices() });
      Notification.itemDeletedNotification();
    },
  });
};

export const useMarkAsSeenDashboardNotices = () => {
  return useMutation({
    mutationKey: DASHBOARD_QUERY_KEYS.markAsSeenDashboardNotices(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: { noticeIds: UUID[] }) => {
      const { data } = await dashboardApi.markAsSeenDashboardNotices(payload);

      return data;
    },
  });
};

export const useMarkAllAsSeenDashboardNotices = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: DASHBOARD_QUERY_KEYS.markAllAsSeenDashboardNotices(),
    meta: { globalSpinner: false },
    mutationFn: () => {
      return dashboardApi.markAllAsSeenDashboardNotices();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DASHBOARD_QUERY_KEYS.filterDashboardNotices() });
    },
  });
};

export const useToggleSeenDashboardNotice = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: DASHBOARD_QUERY_KEYS.toggleSeenDashboardNotice(),
    meta: { globalSpinner: false },
    mutationFn: async (id: UUID) => {
      const { data } = await dashboardApi.toggleSeenDashboardNotice({ id });

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DASHBOARD_QUERY_KEYS.filterDashboardNotices() });
    },
  });
};

export const useAddDashboardNoticeAttachments = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: DASHBOARD_QUERY_KEYS.addDashboardNoticeAttachments(),
    mutationFn: async (payload: EntityObject<FormData>) => {
      const { data } = await dashboardApi.addDashboardNoticeAttachments(payload);

      return data;
    },
    onSuccess: (data, mutatedPayload) => {
      queryClient.setQueryData(
        DASHBOARD_QUERY_KEYS.filterDashboardNotices(),
        (oldData: InfiniteData<DashboardNoticeFilterPageResult>) => ({
          ...oldData,
          pages: oldData.pages.map((page) => {
            if (!page.pageData.some((notice) => notice.id === mutatedPayload.id)) {
              return page;
            }

            return {
              ...page,
              pageData: page.pageData.map((notice) => {
                if (notice.id !== mutatedPayload.id) {
                  return notice;
                }

                return {
                  ...notice,
                  attachments: data,
                };
              }),
            };
          }),
        }),
      );
    },
  });
};

export const useDeleteDashboardNoticeAttachment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: DASHBOARD_QUERY_KEYS.deleteDashboardNoticeAttachment(),
    mutationFn: async (payload: TwoLevelEntityIdObject) => {
      const { data } = await dashboardApi.deleteDashboardNoticeAttachment(payload);

      return data;
    },
    onSuccess: (_, mutatedPayload) => {
      queryClient.setQueryData(
        DASHBOARD_QUERY_KEYS.filterDashboardNotices(),
        (oldData: InfiniteData<DashboardNoticeFilterPageResult>) => ({
          ...oldData,
          pages: oldData.pages.map((page) => {
            if (!page.pageData.some((notice) => notice.id === mutatedPayload.id1)) {
              return page;
            }

            return {
              ...page,
              pageData: page.pageData.map((notice) => {
                if (notice.id !== mutatedPayload.id1) {
                  return notice;
                }

                return {
                  ...notice,
                  attachments: notice.attachments.filter((att) => att.id !== mutatedPayload.id2),
                };
              }),
            };
          }),
        }),
      );
    },
  });
};

export const useSearchDashboardContacts = (filter: DashboardContactSearchRequest) => {
  return useQuery({
    queryKey: DASHBOARD_QUERY_KEYS.searchDashboardContacts(filter),
    meta: { globalSpinner: false },
    queryFn: async () => {
      const { data } = await dashboardApi.searchDashboardContacts(filter);

      return data;
    },
    staleTime: THREE_HOURS,
  });
};

export const useCreateDashboardContact = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: DASHBOARD_QUERY_KEYS.createDashboardContact(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: CreateUpdateDashboardContact) => {
      const { data } = await dashboardApi.createDashboardContact(payload);

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DASHBOARD_QUERY_KEYS.searchDashboardContacts() });
      Notification.itemCreatedNotification();
    },
  });
};

export const useUpdateDashboardContact = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: DASHBOARD_QUERY_KEYS.updateDashboardContact(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: EntityObject<CreateUpdateDashboardContact>) => {
      const { data } = await dashboardApi.updateDashboardContact(payload);

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DASHBOARD_QUERY_KEYS.searchDashboardContacts() });
      Notification.itemUpdatedNotification();
    },
  });
};

export const useDeleteDashboardContact = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: DASHBOARD_QUERY_KEYS.deleteDashboardContact(),
    mutationFn: async (id: string) => {
      return dashboardApi.deleteDashboardContact({ id });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DASHBOARD_QUERY_KEYS.searchDashboardContacts() });
      Notification.itemDeletedNotification();
    },
  });
};

export const useGetDashboardGoals = (payload: EntityObject<DashboardGoalRequest>) => {
  return useQuery({
    queryKey: DASHBOARD_QUERY_KEYS.getDashboardGoals(payload),
    meta: { globalSpinner: false },
    queryFn: async () => {
      const { data } = await dashboardApi.getDashboardGoals(payload);

      return data;
    },
    enabled: !!payload.id.length,
    staleTime: THREE_HOURS,
    placeholderData: keepPreviousData,
  });
};

export const useCreateDashboardGoal = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: DASHBOARD_QUERY_KEYS.createDashboardGoal(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: EntityObject<CreateDashboardGoal>) => {
      const { data } = await dashboardApi.createDashboardGoal(payload);

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DASHBOARD_QUERY_KEYS.getDashboardGoals() });
      Notification.itemCreatedNotification();
    },
  });
};

export const useUpdateDashboardGoal = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: DASHBOARD_QUERY_KEYS.updateDashboardGoal(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: TwoLevelEntityObject<UpdateDashboardGoal>) => {
      const { data } = await dashboardApi.updateDashboardGoal(payload);

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DASHBOARD_QUERY_KEYS.getDashboardGoals() });
      Notification.itemUpdatedNotification();
    },
  });
};

export const useDeleteDashboardGoal = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: DASHBOARD_QUERY_KEYS.deleteDashboardGoal(),
    mutationFn: (payload: TwoLevelEntityIdObject) => {
      return dashboardApi.deleteDashboardGoal(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DASHBOARD_QUERY_KEYS.getDashboardGoals() });
      Notification.itemDeletedNotification();
    },
  });
};

export const useGetDashboardWidgetSettings = () => {
  return useQuery({
    queryKey: DASHBOARD_QUERY_KEYS.getDashboardWidgetSettings(),
    queryFn: async () => {
      const { data } = await dashboardApi.getDashboardWidgetSettings();

      return data;
    },
  });
};

export const useUpdateDashboardWidgetSettings = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: DASHBOARD_QUERY_KEYS.updateDashboardWidgetSettings(),
    mutationFn: async (payload: UpdateDashboardWidgetSettings[]) => {
      const { data } = await dashboardApi.updateDashboardWidgetSettings(payload);

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DASHBOARD_QUERY_KEYS.getDashboardWidgetSettings() });
      Notification.itemUpdatedNotification();
    },
  });
};

export const useGetDefaultDashboardWidgetSettings = () => {
  return useMutation({
    mutationKey: DASHBOARD_QUERY_KEYS.getDefaultDashboardWidgetSettings(),
    mutationFn: async () => {
      const { data } = await dashboardApi.getDefaultDashboardWidgetSettings();

      return data;
    },
  });
};
